// 名も無き亡霊の斬り (shadeSlash) を滑らかにする版 (2026-10-28 比較版・10-29 作者「配布版に入れていいです」で配布版の既定)。
//   ?shadeSlashSmooth=old (開発版だけ) = 前の配布版 (踏み込みは等速・戻りは振り抜きのまま止める)。
//   計画 = docs/research/2026-10-28-smooth-frame-plan-hound-knight-shade.md の亡霊の行。測った結果 = docs/research/2026-10-28-shade-slash-smooth.md。
//   配布版の斬りを撮って分かった事 (scripts/capture_smooth_compare_1028.mjs):
//     ①踏み込みは当たる絵のまま等速 (1コマ 32px) で 230px 進み、着いたコマで急に止まる。
//     ②戻りの 900ms は振り抜いた絵 (slash_follow) のまま完全に止まり、終わりの1コマで待機の絵へ替わる (外枠の飛び 46px)。
//   この版でやる事は①だけ: 踏み込みの見た目を「速く出て減速して止まる」にする。体の移動 (actionAnchorX → lungeTargetX) に対し、
//     絵だけ ease-out で先へ出し、攻撃の終わりで体の位置に着く (体の位置・当たり・秒数は配布版のまま・絵の先行は最大 約27px)。
//   ②は時間の割り振りでは消えない: 振り抜きの絵は前後の袖が広く (横幅 168px)、待機の絵は細い (74px)。足元を支点に傾けると
//     どちら向きでも片側の袖が外へ出て、飛びは 46px → 70px に増えた。埋めるには段の絵が要る (計画の手順3)。
import type { Boss } from './types'

// 1029 (配布版の既定・10-29 作者が動画に「OK」・前の 1028 は開発版 ?shadeSlashSmooth=1028): 作者 10-29「カクカクカクって戻ってる」への直し。1028 の戻りは
//   振り抜き 25コマ→段A 14コマ→段B 15コマが全部止まった絵で、絵の替わり目だけ外枠が 12〜19px 跳ぶ。
//   剣舞の一撃目は体が毎コマ動き、動いている途中で絵が替わり、減速して止まる (docs/research/2026-10-29-knight-turn-butt-and-shade-return.md)。
//   1029 は戻りの間ずっと体を流す: 振り抜きの勢いで前へ少し流れて沈み、段の絵へ替わる間も動き続け、減速して待機の位置へ着く。
type ShadeSlashSmoothMode = 'old' | '1028' | '1029'
let mode: ShadeSlashSmoothMode = '1029'
/** DEV の URL の値を受け取る (game.ts の DEV ブロックが ?shadeSlashSmooth= で呼ぶ)。指定なし = 配布版 (1029・10-29 作者「OK」)。1028 = その前の配布版。 */
export function setShadeSlashSmooth(v: string | null): void { mode = v === 'old' ? 'old' : v === '1028' ? '1028' : '1029' }
export function shadeSlashSmoothMode(): ShadeSlashSmoothMode { return mode }

const easeOut = (u: number): number => 1 - (1 - u) * (1 - u)
const smoothstep = (a: number, b: number, u: number): number => {
  const t = Math.min(1, Math.max(0, (u - a) / (b - a)))
  return t * t * (3 - 2 * t)
}

// 1029 の戻りの流れ: 振り抜きの勢いで前へ最大 RECOVER_DRIFT_PX 流れて沈み (最大 RECOVER_SINK_PX・下が正)、
//   0.25 で折り返して、残りで減速しながら元の位置へ戻る。段の絵の替わり目 (0.45・0.72) は戻りの速い所に入る。
const RECOVER_DRIFT_PX = 12
const RECOVER_SINK_PX = 7
const RECOVER_TURN = 0.25
const recoverShape = (u: number): number => smoothstep(0, RECOVER_TURN, u) * (1 - smoothstep(RECOVER_TURN, 0.95, u))
const recoverU = (b: Pick<Boss, 'state' | 'currentAttack' | 'stateElapsed'>): number | null => {
  const atk = b.currentAttack
  if (mode !== '1029' || !atk || atk.id !== 'shadeSlash' || b.state !== 'recovery') return null
  return Math.min(1, Math.max(0, (b.stateElapsed * 1000) / atk.recoveryMs))
}

/** 絵を画面の横へずらす量 (右が正)。1028 は踏み込みの間だけ、1029 は戻りの間も 0 以外 (old は常に 0)。 */
export function shadeSlashDx(b: Pick<Boss, 'state' | 'currentAttack' | 'stateElapsed' | 'x' | 'actionAnchorX' | 'lungeTargetX' | 'facing'>): number {
  if (mode === 'old') return 0
  const ru = recoverU(b)
  if (ru !== null) {
    const forward = b.facing === 'left' ? -1 : 1
    return forward * RECOVER_DRIFT_PX * recoverShape(ru)
  }
  const atk = b.currentAttack
  if (!atk || atk.id !== 'shadeSlash' || b.state !== 'active') return 0
  const u = Math.min(1, Math.max(0, (b.stateElapsed * 1000) / atk.activeMs))
  return b.actionAnchorX + (b.lungeTargetX - b.actionAnchorX) * easeOut(u) - b.x
}

/** 絵を画面の縦へずらす量 (下が正)。1029 の戻りだけ: 沈んで起き上がる。 */
export function shadeSlashDy(b: Pick<Boss, 'state' | 'currentAttack' | 'stateElapsed'>): number {
  const ru = recoverU(b)
  if (ru === null) return 0
  return RECOVER_SINK_PX * recoverShape(ru)
}

// 段の絵 (計画の手順3・Codex 2026-10-28): 振り抜き→待機の間に、袖を下ろしながら体を起こす2枚を差す。
//   戻りの前半は振り抜きの絵のまま止め (介錯 1028e の決まり=形の違う絵を3〜4コマで替えない)、
//   後半を段A→段Bに分ける。足元 (霧の裾の付け根) は絵の側で動かさない。
export type ShadeSlashStepKey = 'slash_step_a' | 'slash_step_b'
export const SHADE_SLASH_STEP_KEYS: ShadeSlashStepKey[] = ['slash_step_a', 'slash_step_b']
const STEP_A_FROM = 0.45
const STEP_B_FROM = 0.72

/** 戻りで見せる段の絵の名前。old・ほかの技・戻り以外は null (いつもの選び方に任せる)。 */
export function shadeSlashStepKey(b: Pick<Boss, 'state' | 'currentAttack' | 'stateElapsed' | 'hp'>): ShadeSlashStepKey | null {
  if (mode !== '1028' || b.hp <= 0) return null   // 1029 は shadeSlashBlend が選ぶ
  const atk = b.currentAttack
  if (!atk || atk.id !== 'shadeSlash' || b.state !== 'recovery') return null
  const u = (b.stateElapsed * 1000) / atk.recoveryMs
  if (u >= STEP_B_FROM) return 'slash_step_b'
  if (u >= STEP_A_FROM) return 'slash_step_a'
  return null
}

// 1029 の溶け替わり: 亡霊は霧の体なので、絵を1コマで差し替えず、前の絵が溶けながら次の絵が浮かぶ (各 約90ms)。
//   1028 の替わり目は外枠が 12〜19px 跳ぶ。戻りの体の流れは毎コマ 1〜2px しか無く、跳びを動きで隠せない (剣舞は 17px/コマで動く途中に替える)。
//   並び = 振り抜き → (溶け) 段A → (溶け) 段B → (溶け) 待機。待機への溶けは戻りの終わりまでに済ませる。
export type ShadeSlashBlendKey = ShadeSlashStepKey | 'slash_follow' | 'idle'
const BLEND_SEGMENTS: { from: ShadeSlashBlendKey; to: ShadeSlashBlendKey; u0: number; u1: number }[] = [
  { from: 'slash_follow', to: 'slash_step_a', u0: 0.40, u1: 0.50 },
  { from: 'slash_step_a', to: 'slash_step_b', u0: 0.64, u1: 0.74 },
  { from: 'slash_step_b', to: 'idle', u0: 0.88, u1: 0.98 },
]

// 顔 (フードの中の暗い穴) の位置。足元の支点からの画面 px (素材の左向きのまま・SHADE_H 230 の時)。
//   素材 PNG の暗い穴の重心から計算した (x = (顔x − 幅/2)·230/高さ、y = −(高さ − 顔y)·230/高さ)。
//   溶けている間は2枚の顔を重ねて同じ所から滑らせる (ずらさずに重ねると頭が2つ並んで見えた)。
const HEAD_AT: Record<ShadeSlashBlendKey, { x: number; y: number }> = {
  slash_follow: { x: -33.5, y: -193.4 },
  slash_step_a: { x: -38.0, y: -196.2 },
  slash_step_b: { x: -25.9, y: -199.4 },
  idle: { x: -10.3, y: -201.0 },
}

/** 1029 の戻りで描く絵: key (上に描く・濃さ toAlpha) と、溶けている間だけ from (下に描く・濃さ fromAlpha)。
 *  fromDx/fromDy・toDx/toDy は顔を重ねるための画面のずらし (右・下が正)。1029 以外・戻り以外は null。 */
export function shadeSlashBlend(b: Pick<Boss, 'state' | 'currentAttack' | 'stateElapsed' | 'hp' | 'facing'>):
  { key: ShadeSlashBlendKey; from: ShadeSlashBlendKey | null; toAlpha: number; fromAlpha: number; fromDx: number; fromDy: number; toDx: number; toDy: number } | null {
  if (b.hp <= 0) return null
  const u = recoverU(b)
  if (u === null) return null
  let key: ShadeSlashBlendKey = 'slash_follow'
  for (const s of BLEND_SEGMENTS) {
    if (u < s.u0) break
    if (u < s.u1) {
      const t = smoothstep(s.u0, s.u1, u)
      const mirror = b.facing === 'right' ? -1 : 1
      const dx = (HEAD_AT[s.to].x - HEAD_AT[s.from].x) * mirror
      const dy = HEAD_AT[s.to].y - HEAD_AT[s.from].y
      // 2枚を重ねた所の濃さが1枚の時とほぼ同じになる割合 (幽霊 0.82 を2回重ねると濃くなりすぎる)。
      // 顔: 前の絵は t に合わせて次の顔の所へ、次の絵は前の顔の所から本来の所へ滑る (どの瞬間も顔は1つ)。
      return {
        key: s.to, from: s.from, toAlpha: Math.min(1, 1.4 * t), fromAlpha: Math.min(1, 1.4 * (1 - t)),
        fromDx: t * dx, fromDy: t * dy, toDx: -(1 - t) * dx, toDy: -(1 - t) * dy,
      }
    }
    key = s.to
  }
  return { key, from: null, toAlpha: 1, fromAlpha: 0, fromDx: 0, fromDy: 0, toDx: 0, toDy: 0 }
}
